import { createRouter, createWebHistory } from 'vue-router'

import { hasToken, setToken, stashToken } from '@/libs/api'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior: () => ({ top: 0 }),
    routes: [
        {
            // Where the link in the mail lands: the token moves from the address into
            // sessionStorage and out of the visible URL, so it is not left in a shared
            // screen's address bar or a screenshot.
            path: '/r/:token',
            name: 'entry',
            redirect: to => {
                setToken(String(to.params.token ?? ''))

                return { name: 'overview' }
            },
        },
        {
            // The ordering company's rooming link: its own token, its own page —
            // stashed the same way the guest token is, for the same reasons.
            path: '/rooming/:token',
            name: 'rooming-entry',
            redirect: to => {
                stashToken('rooming.token', String(to.params.token ?? ''))

                return { name: 'rooming' }
            },
        },
        // The presentation server's guest (views/Demo.vue, Logic/Demo.go).
        { path: '/demo', name: 'demo', component: () => import('@/views/Demo.vue') },
        { path: '/vnos-gostov', name: 'rooming', component: () => import('@/views/Rooming.vue') },
        {
            // The offer link: same stash-and-redirect as the rooming link.
            path: '/ponudba/:token',
            name: 'offer-entry',
            redirect: to => {
                stashToken('offer.token', String(to.params.token ?? ''))

                return { name: 'offer' }
            },
        },
        { path: '/ponudba-skupine', name: 'offer', component: () => import('@/views/Offer.vue') },
        { path: '/',        name: 'overview', component: () => import('@/views/Overview.vue') },
        { path: '/prijava',  name: 'checkin',  component: () => import('@/views/CheckIn.vue') },
        { path: '/bivanje',  name: 'stay',     component: () => import('@/views/Stay.vue') },
        { path: '/prihod',   name: 'arrival',  component: () => import('@/views/Arrival.vue') },
        { path: '/storitve', name: 'services', component: () => import('@/views/Services.vue') },
        // Supper in the room, ordered from the telephone (docs/PLAN_GOST.md).
        { path: '/strezba',  name: 'roomservice', component: () => import('@/views/RoomService.vue') },
        // The house itself — the room television's content on the phone. Slovenian
        // path like every other page here; the hostnames are the English ones.
        { path: '/hisa',     name: 'house',    component: () => import('@/views/House.vue') },
        { path: '/placila',  name: 'payments', component: () => import('@/views/Payments.vue') },
        { path: '/kljuci',  name: 'keys',     component: () => import('@/views/Keys.vue') },
        // Public on purpose: where a desk-made Stripe link lands. The payer holds
        // no token — the page reads only the query string and shows a sentence.
        { path: '/placilo', name: 'payment-done', component: () => import('@/views/PaymentDone.vue') },
        // The link's second life: the anonymised memory of a stay that is over
        // (docs/POTEK_GOSTA.md §9b). Reached by the guard below rather than by a menu —
        // there is no navigation on this page and nothing else to go to.
        { path: '/spomin',  name: 'memory',   component: () => import('@/views/Memory.vue') },
        { path: '/:rest(.*)*', redirect: { name: 'overview' } },
    ],
})

// The pages that belong to somebody other than a guest holding a stay: the entry
// redirects, the card-payment landing, and the two links a client or a group leader
// opens. None of them is subject to either rule below.
const OPEN_ROUTES = ['demo', 'entry', 'payment-done', 'rooming-entry', 'rooming', 'offer-entry', 'offer']

// Two rules, and both are guards rather than per-view checks so that a screen added
// next year cannot forget either.
//
//  1. Without a token every page is the same page: the explanation.
//  2. Once the stay is over every page is the memory. The service refuses the stay's
//     own doors from that moment (registerGuestPortalRoutes), so a guest who followed a
//     bookmark to /kljuci would otherwise meet a screen with nothing on it — and the
//     answer to "where is my key" months after leaving is not an empty page.
//
// The mode is only known once the stay read has answered, which is why this redirects
// on what the store HOLDS and never waits for it: App.vue starts that read, and the
// screen it lands on is corrected the moment the answer arrives.
router.beforeEach((to) => {
    if (OPEN_ROUTES.includes(String(to.name))) return true

    if (!hasToken() && to.name !== 'invalid') return { name: 'invalid' }

    if (useAppStore().mode === 'memory' && to.name !== 'memory' && to.name !== 'invalid') {
        return { name: 'memory' }
    }

    return true
})

router.addRoute({ path: '/neveljavna', name: 'invalid', component: () => import('@/views/Invalid.vue') })

/**
 * A page opened before a deploy asks for the chunks of the build it was loaded with, and
 * the deploy has removed them: the import fails, the navigation is abandoned, and a tap
 * on a menu entry does nothing at all — found on a telephone on 26. 9. 2026, the evening
 * a deploy landed between two taps. The only repair is the new index.html, so the page
 * is reloaded ON the address that was asked for. Once per address, so a chunk that is
 * genuinely missing is a failed page and not a reload loop.
 */
const reloadInto = (path: string) => {
    const key = 'guest.chunk-reload'

    if (sessionStorage.getItem(key) === path) return

    sessionStorage.setItem(key, path)
    window.location.assign(path)
}

router.onError((error, to) => {
    const message = String((error as Error)?.message ?? error)

    if (/dynamically imported module|Importing a module script failed|error loading dynamically|Failed to fetch/i.test(message)) {
        reloadInto(to.fullPath)
    }
})

router.afterEach(() => sessionStorage.removeItem('guest.chunk-reload'))

export default router
